import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { useChangePassword } from "@/domains/profile/mutations/use-change-password";
import { useProfile } from "@/domains/profile/queries/use-profile";
import { Button, PasswordInput } from "@/ui";
import {
  testPasswordContainsNumber,
  testPasswordContainsSpecialCharacter,
  testPasswordContainsUppercaseLetter,
  testPasswordLength,
} from "@/utils";
import { ConfirmChangePasswordAlert } from "./modals/confirm-change-password-alert";

const schema = z
  .object({
    password: z.string().min(1, { message: "Please enter your password" }),
    newPassword: z.string(),
    confirmNewPassword: z.string(),
  })
  .refine(
    (data) => {
      return testPasswordLength(data.newPassword);
    },
    {
      message: "The new password is too short",
      path: ["newPassword"],
    },
  )
  .refine(
    (data) => {
      return testPasswordContainsUppercaseLetter(data.newPassword);
    },
    {
      message: "The new password must contain a capital letter",
      path: ["newPassword"],
    },
  )
  .refine(
    (data) => {
      return testPasswordContainsNumber(data.newPassword);
    },
    {
      message: "The new password must contain a number",
      path: ["newPassword"],
    },
  )
  .refine(
    (data) => {
      return testPasswordContainsSpecialCharacter(data.newPassword);
    },
    {
      message: "The new password must contain a special character",
      path: ["newPassword"],
    },
  )
  .refine(
    (data) => {
      return data.newPassword !== data.password;
    },
    {
      message: "The new password can not be the same as the old one",
      path: ["newPassword"],
    },
  )
  .refine(
    (data) => {
      return data.newPassword === data.confirmNewPassword;
    },
    {
      message: "The new password and its confirmation do not match",
      path: ["confirmNewPassword"],
    },
  );

type ChangePasswordFormData = z.infer<typeof schema>;

export const ChangePasswordForm = () => {
  const navigate = useNavigate();
  const { data: profile } = useProfile();

  const email = profile?.email;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<ChangePasswordFormData>();

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(schema),
  });

  const { mutate: changePassword } = useChangePassword();

  const submitPassword = (data: ChangePasswordFormData) => {
    setFormData(data);
    setIsModalOpen(true);
  };

  const confirmChangePassword = () => {
    if (!email || !formData) {
      return;
    }
    changePassword(
      {
        email,
        currentPassword: formData.password,
        newPassword: formData.newPassword,
        newPasswordConfirmation: formData.confirmNewPassword,
      },
      {
        onSuccess: () => {
          return navigate({ to: "/more", search: { tab: "basic-information" } });
        },
        onSettled: () => {
          return setIsModalOpen(false);
        },
      },
    );
  };

  return (
    <>
      <ConfirmChangePasswordAlert
        isOpen={isModalOpen}
        onClose={() => {
          return setIsModalOpen(false);
        }}
        onConfirm={confirmChangePassword}
      />

      <form
        className="flex flex-col"
        onSubmit={(e) => {
          void handleSubmit(submitPassword)(e);
        }}
      >
        <p className="mb-4">
          We’ll just need your current password, and then you can enter your new password:
        </p>

        <div className="flex flex-col gap-2 sm:max-w-[327px]">
          <PasswordInput
            autoComplete="password"
            className="rounded-lg"
            error={errors.password?.message}
            errorClassName="px-0"
            id="password"
            label="Current password"
            showDisplayPasswordButton
            {...register("password")}
          />

          <PasswordInput
            autoComplete="new-password"
            className="rounded-lg"
            error={errors.newPassword?.message}
            errorClassName="px-0"
            id="password"
            label="New password"
            showDisplayPasswordButton
            {...register("newPassword")}
          />

          <PasswordInput
            autoComplete="new-password"
            className="rounded-lg"
            error={errors.confirmNewPassword?.message}
            errorClassName="px-0"
            id="password"
            label="Confirm new password"
            showDisplayPasswordButton
            {...register("confirmNewPassword")}
          />

          <Button className="mt-2 w-full" size="lg" type="submit" variant="primary">
            Change password
          </Button>
        </div>
      </form>
    </>
  );
};
